'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import Navbar from '../components/Navbar'
import { useAuth } from '../contexts/AuthContext'
import { authenticatedFetch, storeSessionTokenFrom } from '@/lib/client-fetch'

// The iOS bundle (NEXT_PUBLIC_MOBILE_UI_ONLY) ships the parent/* pages but not
// rider/*, so parents land on the parent flow there. Web keeps upstream routes.
const isMobileShell = process.env.NEXT_PUBLIC_MOBILE_UI_ONLY === 'true'

function landingPathFor(type: string | undefined): string | null {
  if (type === 'student') return '/student/orders'
  if (type === 'parent') return isMobileShell ? '/parent/new-order' : '/rider/new-order'
  if (type === 'driver') return '/driver/schedule'
  if (type === 'admin') return '/admin'
  if (type === 'promo_partner') return '/promo-partner/dashboard'
  return null
}

export default function LoginPage() {
  const router = useRouter()
  const { user, login, isLoading } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)

  // Check for success message from password reset or email prefill
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('passwordSet') === 'true') {
      setSuccess('Password has been set successfully! You can now log in.')
      // Clean up URL
      router.replace('/login', undefined)
    }
    // Pre-fill email if provided (e.g., from promo partner signup)
    const emailParam = urlParams.get('email')
    if (emailParam) {
      setEmail(emailParam)
    }
  }, [router])

  // Redirect if already logged in
  useEffect(() => {
    if (!isLoading && user) {
      // Redirect based on user type
      const dest = landingPathFor(user.type)
      if (dest) router.push(dest)
    }
  }, [user, isLoading, router])

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="text-ink-soft">Loading...</p>
      </div>
    )
  }

  // Don't show login form if already logged in (will redirect)
  if (user) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="text-ink-soft">Redirecting...</p>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Database login (QA accounts now handled server-side if ALLOW_QA_AUTH is set)
      // Web: the server sets the session cookie. Native: the response also
      // carries `token`, which is stored and sent as a bearer from now on.
      const response = await authenticatedFetch('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Invalid email or password')
      }

      if (data.success && data.user) {
        storeSessionTokenFrom(data)

        // Session is established, now fetch user from /me to hydrate state
        const meResponse = await authenticatedFetch('/api/auth/me', { method: 'GET' })

        if (meResponse.ok) {
          const meData = await meResponse.json()
          login(meData.user)
        } else {
          // Fallback to using returned user data
          login(data.user)
        }

        // Redirect based on user type
        const dest = landingPathFor(data.user.type)
        if (dest) router.push(dest)
      }
    } catch (err: any) {
      setError(err.message || 'Invalid email or password')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      <div className="max-w-md mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-12">
        <div className="mb-6 sm:mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight text-ink mb-2">Sign In</h1>
          <p className="text-sm sm:text-base text-ink-soft">Welcome back to Deeliver</p>
        </div>

        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg shadow-ink/[0.06] border border-cream-deep">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-gray-700 text-sm mb-2 font-medium">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full bg-white text-ink px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm mb-2 font-medium">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full bg-white text-ink px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
              />
            </div>

            {success && (
              <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded-lg text-sm">
                {success}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-ember text-white py-3 rounded-full font-semibold hover:bg-ember-hover transition-colors shadow-md shadow-ember/25 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            <div className="text-center">
              <Link 
                href="/forgot-password" 
                className="text-ember hover:text-ember-hover hover:underline font-medium text-sm"
              >
                Forgot Password?
              </Link>
            </div>
          </form>
        </div>

        <p className="text-ink-soft text-center mt-6">
          Don't have an account? <Link href="/register" className="text-ember hover:text-ember-hover hover:underline font-medium">Register</Link>
        </p>
      </div>
    </div>
  )
}
